import React from 'react';
import styled from 'styled-components';
import { Mail, Phone, MapPin, Github, Linkedin } from 'lucide-react';
import { theme } from '../styles/theme';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';

const ContactHeader = styled.div`
  background: ${theme.colors.backgroundAlt};
  background-image: radial-gradient(circle at 40% 40%, rgba(58, 123, 213, 0.04) 0%, transparent 50%);
  padding: ${theme.spacing['3xl']} 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  text-align: center;
`;

const HeaderTitle = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['3xl']};
  }
`;

const HeaderSubtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContactSection = styled.section`
  padding: ${theme.spacing['3xl']} 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
`;

const ContactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const ContactCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  border: 1px solid ${theme.colors.border};
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ContactTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const ContactItemList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing['2xl']};
  width: 100%;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ContactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
  text-align: center;
`;

const ContactIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary}20;
  color: ${theme.colors.primary};
`;

const ContactText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const ContactLabel = styled.span`
  font-weight: 600;
  color: ${theme.colors.text};
  font-size: ${theme.fontSizes.base};
`;

const ContactValue = styled.span`
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.sm};
`;

const SocialSection = styled.div`
  text-align: center;
`;

const SocialTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.surface};
  color: ${theme.colors.textLight};
  transition: all 0.15s ease;
  border: 1px solid ${theme.colors.border};

  &:hover {
    background: ${theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

export const Contact: React.FC = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://your-domain.vercel.app';

  return (
    <>
      <SEO
        title="Contact"
        description="Contact Antonio Michal Verdile for professional opportunities, collaboration, and technical consultations. Available via email, LinkedIn, and GitHub."
        canonical={`${siteUrl}/contact`}
      />
      <StructuredData 
        type="page" 
        breadcrumbs={[
          { name: 'Home', url: siteUrl },
          { name: 'Contact', url: `${siteUrl}/contact` }
        ]}
      />
      
      <ContactHeader>
        <HeaderContent>
          <HeaderTitle>Contact</HeaderTitle>
          <HeaderSubtitle>
            I'm available for professional opportunities, collaboration, and technical consultations. 
            Please feel free to reach out through any of the channels below.
          </HeaderSubtitle>
        </HeaderContent>
      </ContactHeader>

      <ContactSection>
        <Container>
          <ContactGrid>
            <ContactInfo>
              <ContactCard>
                <ContactTitle>Contact Information</ContactTitle>
                <ContactItemList>
                  <ContactItem>
                    <ContactIconWrapper>
                      <Mail size={20} />
                    </ContactIconWrapper>
                    <ContactText>
                      <ContactLabel>Email</ContactLabel>
                      <ContactValue>michaelverdile1@gmail.com</ContactValue>
                    </ContactText>
                  </ContactItem>
                  
                  <ContactItem>
                    <ContactIconWrapper>
                      <Phone size={20} />
                    </ContactIconWrapper>
                    <ContactText>
                      <ContactLabel>LinkedIn</ContactLabel>
                      <ContactValue>linkedin.com/in/michal-verdile-61b813151</ContactValue>
                    </ContactText>
                  </ContactItem>
                  
                  <ContactItem>
                    <ContactIconWrapper>
                      <MapPin size={20} />
                    </ContactIconWrapper>
                    <ContactText>
                      <ContactLabel>Location</ContactLabel>
                      <ContactValue>Switzerland</ContactValue>
                    </ContactText>
                  </ContactItem>
                </ContactItemList>
              </ContactCard>

              <ContactCard>
                <SocialSection>
                  <SocialTitle>Professional Networks</SocialTitle>
                  <SocialLinks>
                    <SocialLink 
                      href="https://github.com/MichalVerdile" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="GitHub"
                    >
                      <Github size={20} />
                    </SocialLink>
                    <SocialLink 
                      href="https://linkedin.com/in/michal-verdile-61b813151" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                    >
                      <Linkedin size={20} />
                    </SocialLink>
                    <SocialLink 
                      href="mailto:michaelverdile1@gmail.com" 
                      aria-label="Email"
                    >
                      <Mail size={20} />
                    </SocialLink>
                  </SocialLinks>
                </SocialSection>
              </ContactCard>
            </ContactInfo>
          </ContactGrid>
        </Container>
      </ContactSection>
    </>
  );
};